<template>
  <section class="main-notice-detail-ko-section">
    <div class="title">
      Notice of Account Integration & <br />
      Mandatory Identity Verification
    </div>
    <div class="main-section">
      <div class="info-section">
        <p>
          <span class="focus-text">
            Hello from USIMSA, trusted by over 7 million travelers worldwide.
          </span>
          <br />
          Thank you for your continued support.
        </p>

        <p>
          To provide a more secure experience, we're rolling out an
          <span class="focus-text"> Account Integration</span> update on
          <span class="focus-text">Friday, May 8, 2026</span>.
        </p>
        <p>
          As part of this update, mobile identity verification through Korean telecom providers will
          be required for all logins. Customers unable to complete this verification will not be
          able to log in to USIMSA after the update.
        </p>
        <p>We apologize for any inconvenience and ask that you review the information below.</p>
      </div>
      <div class="hr-section"></div>
      <div class="auth-possible-section">
        <h4 class="auth-possible-title">
          ✅ For Customers Who Can Verify Their Identity (Via Korean Telecoms)
        </h4>
        <ul class="mt-default">
          <li>
            <span class="focus-text">Eligible: </span>Subscribers of SKT, KT, LGU+, and Korean MVNOs
          </li>
          <li>You can continue using all services as usual.</li>
          <li>
            Just complete a one-time identity verification after May 8 to finish account
            integration.
          </li>
        </ul>
      </div>
      <div class="hr-section"></div>
      <div class="auth-impossible-section">
        <h4 class="auth-impossible-title">⚠️ Important Information for Affected Customers</h4>
        <div class="auth-impossible-range mt-default">
          <h5>💡 Who Does This Affect?</h5>
          <ul>
            <li>Customers with international (non-Korean) mobile numbers only</li>
            <li>
              Customers using a phone registered under another person's name (e.g., a family member)
            </li>
            <li>
              Users of prepaid or corporate phones that don't support Korean carrier verification
            </li>
          </ul>
        </div>
        <div class="auth-impossible-des mt-default">
          <ul>
            <li>
              Purchase History & Customer Support
              <p>
                Don't worry—if you can't log in or need help checking your purchase history after
                the update, our 24/7 Customer Center is here to help.
              </p>
            </li>
            <li>
              Save Your Unused eSIM QR Codes
              <p>
                If you have eSIMs you haven't used yet, you may have trouble accessing them after
                the update. To stay connected, please open and screenshot your QR codes before May
                8.
              </p>
            </li>
            <li>
              Coupons & Reward Points
              <p>
                Any unused coupons and reward points will be inaccessible after the update due to
                login restrictions. We strongly recommend using them before May 8.
              </p>
            </li>
            <li>
              Keep Shopping with Guest Checkout
              <p>
                You can still use USIMSA without logging in—just select
                <span class="focus-text">'Guest Checkout'</span> at payment. No identity
                verification needed to buy eSIMs and receive QR codes. (Note: Coupons and reward
                points cannot be applied to guest purchases.)
              </p>
            </li>
            <li>
              Try Superalink — USIMSA's Global Platform
              <p>
                If you travel internationally, check out
                <span class="focus-text">Superalink</span>—our global eSIM service built for
                travelers worldwide. Sign up with your email (no Korean phone number required) to
                access eSIMs for 190+ countries.
              </p>
            </li>
          </ul>
          <p
            class="route-supera"
            @click="emits('moveToSupera')"
          >
            👉 <span class="route-supera-focus">Go to Superalink</span>
          </p>
        </div>
      </div>
      <div class="hr-section"></div>
      <div class="last-info-section">
        <p>If you have any questions, our Customer Center is available around the clock.</p>
        <p>Thank you for choosing USIMSA.</p>
        <h4
          class="channel-home"
          @click="emits('moveToChannelHome')"
        >
          Contact USIMSA Customer Center
        </h4>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const emits = defineEmits<{
  moveToSupera: []
  moveToChannelHome: []
}>()
</script>

<style scoped lang="scss">
.main-notice-detail-ko-section {
  padding: 56px 20px 16px;
  .focus-text {
    font-weight: 700;
  }
  .yellow-text {
    font-weight: 700;
    color: #ff9a12;
  }
  p + p {
    margin-top: 32px;
  }
  .title {
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
  }
  .main-section {
    margin-top: 8px;
    padding: 8px 4px 20px;
    font-size: 16px;
    font-weight: 400;
    line-height: 32px;
    .info-section {
      color: #616161;
    }
  }
  .hr-section {
    margin: 30px 0;
    height: 1px;
    border-bottom: 1px solid #eeeeee;
  }
  .auth-possible-section {
    .auth-possible-title {
      color: #424242;
      font-size: 18px;
      font-weight: 700;
      line-height: 26px;
    }
  }
  .auth-impossible-section {
    font-size: 16px;
    .auth-impossible-title {
      color: #ff9a12;
      font-size: 18px;
      font-weight: 700;
      line-height: 26px;
    }
    .auth-impossible-range {
      padding: 12px;
      background-color: #f9f9f9;
      border-radius: 12px;
      line-height: 32px;
      h5 {
        font-weight: 700;
      }
      li {
        font-weight: 400;
      }
    }
    .auth-impossible-des {
      ul {
        li {
          font-weight: 700;
          p {
            font-weight: 400;
          }
        }
      }
      .route-supera {
        margin-top: 32px;
        font-size: 16px;
        font-weight: 700;
        line-height: 45px;
        cursor: pointer;
        .route-supera-focus {
          text-decoration-line: underline;
          text-decoration-style: solid;
          text-decoration-skip-ink: auto;
          text-decoration-thickness: auto;
          text-underline-offset: auto;
          text-underline-position: from-font;
        }
      }
    }
  }
  .last-info-section {
    .channel-home {
      cursor: pointer;
      margin-top: 30px;
      font-weight: 700;
      line-height: 24px; /* 150% */
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: auto;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
    }
  }
}
</style>
